import React, { useRef, useState } from 'react'
import {
    Box,
    Button,
    Chip,
    CircularProgress,
    Paper,
    Typography,
} from '@mui/material'
import { useTheme } from '@material-ui/core'
import CloudUploadIcon from '@material-ui/icons/CloudUpload'
import GetAppIcon from '@material-ui/icons/GetApp'
import InsertDriveFileIcon from '@material-ui/icons/InsertDriveFile'
import CheckCircleIcon from '@material-ui/icons/CheckCircle'

const ACCEPTED_EXTENSIONS = ['.xlsx', '.csv']

const isAcceptedFile = (file: File) => {
    const name = file.name.toLowerCase()
    return ACCEPTED_EXTENSIONS.some((ext) => name.endsWith(ext))
}

interface Props {
    title: string
    description: string
    columns: string[]
    tips?: string[]
    fileName?: string | null
    uploadedCount?: number
    uploadedLabel?: string
    disabled?: boolean
    disabledReason?: string
    isUploading?: boolean
    onUpload: (file: File) => void
    onDownloadTemplate: () => void
}

export const SheetOnboardUpload = ({
    title,
    description,
    columns,
    tips = [],
    fileName,
    uploadedCount = 0,
    uploadedLabel,
    disabled,
    disabledReason,
    isUploading,
    onUpload,
    onDownloadTemplate,
}: Props) => {
    const theme = useTheme()
    const inputRef = useRef<HTMLInputElement>(null)
    const [isDragging, setIsDragging] = useState(false)
    const hasUpload = uploadedCount > 0 && Boolean(fileName)

    const handleFiles = (files: FileList | null) => {
        if (disabled || isUploading) return
        const file = files?.[0]
        if (!file || !isAcceptedFile(file)) return
        onUpload(file)
    }

    const borderColor = disabled
        ? theme.palette.divider
        : isDragging
        ? theme.palette.primary.main
        : hasUpload
        ? theme.palette.success.light
        : theme.palette.divider

    const background = disabled
        ? '#F3F4F4'
        : isDragging
        ? '#E8F3F1'
        : hasUpload
        ? '#F4FAF6'
        : '#F7F9F8'

    return (
        <Paper
            elevation={0}
            style={{
                marginBottom: theme.spacing(2),
                border: `1px solid ${theme.palette.divider}`,
                borderRadius: 12,
                overflow: 'hidden',
            }}
        >
            <Box
                style={{
                    padding: theme.spacing(2, 2.5, 1.5),
                    borderBottom: `1px solid ${theme.palette.divider}`,
                    backgroundColor: '#FFFFFF',
                }}
            >
                <Box
                    display="flex"
                    alignItems="flex-start"
                    justifyContent="space-between"
                    style={{ gap: 12 }}
                >
                    <Box>
                        <Typography
                            variant="subtitle1"
                            style={{ fontWeight: 700, lineHeight: 1.3 }}
                        >
                            {title}
                        </Typography>
                        <Typography
                            variant="body2"
                            color="textSecondary"
                            style={{ marginTop: 4 }}
                        >
                            {description}
                        </Typography>
                    </Box>
                    <Button
                        size="small"
                        startIcon={<GetAppIcon />}
                        onClick={onDownloadTemplate}
                        style={{ textTransform: 'none', flexShrink: 0 }}
                    >
                        Download template
                    </Button>
                </Box>
                <Box
                    display="flex"
                    flexWrap="wrap"
                    style={{ gap: 6, marginTop: 12 }}
                >
                    {columns.map((column) => (
                        <Chip
                            key={column}
                            size="small"
                            label={column}
                            variant="outlined"
                            style={{ height: 22, fontSize: 12 }}
                        />
                    ))}
                </Box>
                {tips.length > 0 && (
                    <Box
                        component="ul"
                        style={{
                            margin: '12px 0 0',
                            paddingLeft: 18,
                            color: theme.palette.text.secondary,
                        }}
                    >
                        {tips.map((tip) => (
                            <Typography
                                key={tip}
                                component="li"
                                variant="body2"
                                color="textSecondary"
                                style={{ marginBottom: 4 }}
                            >
                                {tip}
                            </Typography>
                        ))}
                    </Box>
                )}
            </Box>

            <Box
                onClick={() => {
                    if (!disabled && !isUploading) {
                        inputRef.current?.click()
                    }
                }}
                onDragEnter={(event) => {
                    event.preventDefault()
                    if (!disabled) setIsDragging(true)
                }}
                onDragOver={(event) => {
                    event.preventDefault()
                    if (!disabled) setIsDragging(true)
                }}
                onDragLeave={() => setIsDragging(false)}
                onDrop={(event) => {
                    event.preventDefault()
                    setIsDragging(false)
                    handleFiles(event.dataTransfer.files)
                }}
                style={{
                    margin: theme.spacing(2),
                    padding: theme.spacing(3, 2),
                    border: `1.5px dashed ${borderColor}`,
                    borderRadius: 10,
                    backgroundColor: background,
                    cursor: disabled || isUploading ? 'not-allowed' : 'pointer',
                    textAlign: 'center',
                    opacity: disabled ? 0.7 : 1,
                    transition:
                        'border-color 120ms ease, background-color 120ms ease',
                }}
            >
                <input
                    ref={inputRef}
                    type="file"
                    accept=".xlsx,.csv"
                    style={{ display: 'none' }}
                    onChange={(event) => {
                        handleFiles(event.target.files)
                        event.target.value = ''
                    }}
                />
                <Box
                    style={{
                        width: 48,
                        height: 48,
                        borderRadius: '50%',
                        margin: '0 auto 12px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        backgroundColor: '#FFFFFF',
                        border: `1px solid ${theme.palette.divider}`,
                    }}
                >
                    {isUploading ? (
                        <CircularProgress size={22} />
                    ) : hasUpload ? (
                        <CheckCircleIcon
                            style={{ color: theme.palette.success.main }}
                        />
                    ) : (
                        <CloudUploadIcon
                            style={{ color: theme.palette.primary.dark }}
                        />
                    )}
                </Box>
                <Typography variant="body2" style={{ fontWeight: 600 }}>
                    {disabled
                        ? disabledReason
                        : isUploading
                        ? 'Uploading…'
                        : isDragging
                        ? 'Drop file to upload'
                        : hasUpload
                        ? 'Replace this file'
                        : 'Drop a file here, or click to browse'}
                </Typography>
                <Typography
                    variant="caption"
                    color="textSecondary"
                    display="block"
                    style={{ marginTop: 4 }}
                >
                    .xlsx or .csv
                </Typography>
                {fileName && (
                    <Box
                        display="inline-flex"
                        alignItems="center"
                        style={{
                            gap: 6,
                            marginTop: 12,
                            padding: '6px 10px',
                            borderRadius: 8,
                            backgroundColor: '#FFFFFF',
                            border: `1px solid ${theme.palette.divider}`,
                        }}
                    >
                        <InsertDriveFileIcon style={{ fontSize: 16 }} />
                        <Typography
                            variant="caption"
                            style={{ fontWeight: 600 }}
                        >
                            {fileName}
                        </Typography>
                        {uploadedLabel && (
                            <Chip
                                size="small"
                                color="primary"
                                label={uploadedLabel}
                                style={{ height: 20, fontSize: 11 }}
                            />
                        )}
                    </Box>
                )}
            </Box>
        </Paper>
    )
}
